import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Textarea,
  TextInput,
  Paper,
  Badge,
  Alert,
  Loader,
  ScrollArea,
  Divider,
  ActionIcon,
} from '@mantine/core';
import { IconFileText, IconPlus, IconAlertCircle, IconCheck, IconChevronLeft } from '@tabler/icons-react';
import { apiFetch } from '../lib/api';

interface Report {
  id: string;
  title: string;
  raw_text: string;
  report_date: { _seconds: number };
  created_at: { _seconds: number };
}

interface ReportLogProps {
  opened: boolean;
  onClose: () => void;
}

export function ReportLog({ opened, onClose }: ReportLogProps) {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [view, setView] = useState<'list' | 'add' | 'detail'>('list');
  const [title, setTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL ?? '';

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`${apiUrl}/api/reports`);
      if (!response.ok) throw new Error('Failed to fetch reports');
      const data = await response.json();
      setReports(data.reports || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (opened) {
      fetchReports();
      setView('list');
      setSuccess(false);
    }
  }, [opened, fetchReports]);

  const handleSubmit = async () => {
    if (!title.trim() || !rawText.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch(`${apiUrl}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), raw_text: rawText.trim() }),
      });

      if (!response.ok) throw new Error('Failed to save report');

      setSuccess(true);
      setTitle('');
      setRawText('');
      setTimeout(() => {
        setSuccess(false);
        setView('list');
        fetchReports();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save report');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (ts: { _seconds: number }) => {
    if (!ts?._seconds) return '—';
    return new Date(ts._seconds * 1000).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          {view !== 'list' && (
            <ActionIcon variant="subtle" size="sm" onClick={() => setView('list')}>
              <IconChevronLeft size={16} />
            </ActionIcon>
          )}
          <IconFileText size={20} />
          <Text fw={600}>
            {view === 'add' ? 'Add Report' : view === 'detail' ? selectedReport?.title : '3PL Reports'}
          </Text>
        </Group>
      }
      size="lg"
    >
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert icon={<IconCheck size={16} />} color="green" mb="md">
          Report saved
        </Alert>
      )}

      {view === 'list' && (
        <Stack>
          <Group justify="flex-end">
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setView('add')}
            >
              Add Report
            </Button>
          </Group>

          {loading ? (
            <Loader size="sm" mx="auto" />
          ) : reports.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">No reports yet</Text>
          ) : (
            <Stack gap="xs">
              {reports.map((report) => (
                <Paper
                  key={report.id}
                  p="sm"
                  withBorder
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedReport(report);
                    setView('detail');
                  }}
                >
                  <Group justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>{report.title}</Text>
                      <Text size="xs" c="dimmed">
                        {formatDate(report.report_date)}
                      </Text>
                    </div>
                    <Badge size="xs" variant="light" color="gray">
                      {report.raw_text.length.toLocaleString()} chars
                    </Badge>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {view === 'add' && (
        <Stack>
          <TextInput
            label="Title"
            placeholder="e.g. Week of Jan 20"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Report"
            placeholder="Paste the 3PL report here..."
            value={rawText}
            onChange={(e) => setRawText(e.currentTarget.value)}
            minRows={12}
            maxRows={20}
            autosize
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setView('list')}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={!title.trim() || !rawText.trim()}
            >
              Save Report
            </Button>
          </Group>
        </Stack>
      )}

      {view === 'detail' && selectedReport && (
        <Stack>
          <Group gap="xs">
            <Badge size="sm" variant="light">{formatDate(selectedReport.report_date)}</Badge>
          </Group>
          <Divider />
          <ScrollArea h={400}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedReport.raw_text}
            </Text>
          </ScrollArea>
        </Stack>
      )}
    </Modal>
  );
}
